import React from "react";
import { Truck, ChevronDown, ChevronRight } from "lucide-react";
import { AssignedCart, Group } from "../../../../redux/deliveryRouteOptimizer";

interface AssignedCartsSectionProps {
  assignedCarts: AssignedCart[];
  groups: Record<string, Group>;
  expandedCarts: Set<string>;
  selectedCartsForMap: string[];
  isPrinting: boolean;
  onToggleCartExpansion: (cartId: string) => void;
  onToggleCartSelection: (cartId: string) => void;
  onShowOnlySelectedCarts: () => void;
  onCartPrint: (
    cartId: string,
    cartName: string,
    copyType: "rider" | "customer"
  ) => void;
  getOptimizedRouteOrder: (group: Group) => string[];
}

const AssignedCartsSection: React.FC<AssignedCartsSectionProps> = ({
  assignedCarts,
  groups,
  expandedCarts,
  selectedCartsForMap,
  isPrinting,
  onToggleCartExpansion,
  onToggleCartSelection,
  onShowOnlySelectedCarts,
  onCartPrint,
  getOptimizedRouteOrder,
}) => {
  // Helper function to check if a group is assigned to a specific cart
  const isGroupAssignedToCart = (group: Group, cartId: string): boolean => {
    if (!group.cart) return false;
    
    // Check if this is the delivery cart ID
    const deliveryCartId = (group.cart as any)?.deliveryCartId;
    
    // Check both deliveryCartId and cartId
    return deliveryCartId === cartId || group.cart.cartId === cartId;
  };

  // Check if a cart has actual order groups (collection groups)
  const cartHasActualGroups = (cartId: string) => {
    const assignedGroups = Object.values(groups).filter(
      (group) => isGroupAssignedToCart(group, cartId) && group.isCollectionGrouped
    );
    return assignedGroups.length > 0;
  };

  if (assignedCarts.length === 0) {
    return (
      <div className="delivery-panel__empty-state">
        <Truck size={32} />
        <p>No assigned carts</p>
        <p>No carts are currently assigned to any groups</p>
      </div>
    );
  }

  return (
    <div className="assigned-carts-container">
      <div className="assigned-carts__list">
        {assignedCarts.map((cart) => {
          // Find ALL groups assigned to this cart (both tentative and collection)
          const assignedGroups = Object.values(groups).filter(
            (group) => isGroupAssignedToCart(group, cart.id)
          );

          // Separate groups by type for display
          const tentativeGroups = assignedGroups.filter(
            (group) => !group.isCollectionGrouped
          );
          const collectionGroups = assignedGroups.filter(
            (group) => group.isCollectionGrouped
          );

          const totalDeliveries = assignedGroups.reduce(
            (total, group) => total + getOptimizedRouteOrder(group).length,
            0
          );

          const isExpanded = expandedCarts.has(cart.id);
          const isCartSelected = selectedCartsForMap.includes(cart.id);
          const hasActualGroups = cartHasActualGroups(cart.id);

          return (
            <div
              key={cart.id}
              className={`cart-item cart-item--assigned cart-item--collapsible ${
                isCartSelected ? "cart-item--selected" : ""
              }`}
            >
              <div
                className="cart-item__header cart-item__header--clickable"
                onClick={() => onToggleCartExpansion(cart.id)}
              >
                <div className="cart-item__info">
                  {/* Checkbox for cart selection */}
                  <div
                    className="cart-item__checkbox-container"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isCartSelected}
                      onChange={() => {
                        onToggleCartSelection(cart.id);
                        if (!selectedCartsForMap.length) {
                          onShowOnlySelectedCarts();
                        }
                      }}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                  </div>

                  <div className="cart-item__details">
                    <div className="cart-item__name">
                      {cart.name}
                      <span className="cart-item__expand-icon">
                        {isExpanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </span>
                    </div>
                    <div className="cart-item__status">
                      <Truck size={12} />
                      <span>
                        Assigned to {assignedGroups.length} group(s) •{" "}
                        {totalDeliveries} orders
                        {hasActualGroups && (
                          <span className="cart-item__actual-badge">
                            • Has Finalized Groups
                          </span>
                        )}
                      </span>
                    </div>
                   
                    {cart.cartState && (
                      <div className="cart-item__state">
                        Status: {cart.cartState}
                      </div>
                    )}
                  </div>
                </div>

                {/* PRINT BUTTONS - Only show if cart has actual groups */}
                {hasActualGroups && (
                  <div
                    className="cart-item__print-buttons"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => onCartPrint(cart.id, cart.name, "rider")}
                      disabled={isPrinting}
                      className="cart-item__print-button cart-item__print-button--rider"
                    >
                      Rider Copy
                    </button>
                    <button
                      onClick={() =>
                        onCartPrint(cart.id, cart.name, "customer")
                      }
                      disabled={isPrinting}
                      className="cart-item__print-button cart-item__print-button--customer"
                    >
                      Customer Copy
                    </button>
                  </div>
                )}
              </div>

              {/* Collapsible Content */}
              {isExpanded && (
                <div className="cart-item__expanded-content">
                  {/* Show tentative groups - GREEN */}
                  {tentativeGroups.length > 0 && (
                    <div className="cart-item__assigned-groups">
                      <div className="cart-item__groups-label">
                        Tentative Groups ({tentativeGroups.length}):
                      </div>
                      <div className="cart-item__groups-list">
                        {tentativeGroups.map((group) => {
                          const optimizedOrderIds =
                            getOptimizedRouteOrder(group);
                          return (
                            <div
                              key={group.id}
                              className="cart-item__group-tag cart-item__group-tag--tentative"
                              style={{
                                color: "#10b981",
                                borderColor: "#10b981",
                                backgroundColor: "#10b98120",
                              }}
                            >
                              {group.name} ({optimizedOrderIds.length} orders)
                              {group.deliveryRoute && (
                                <span className="cart-item__group-distance">
                                  •{" "}
                                  {group.deliveryRoute.distanceInKm.toFixed(2)}{" "}
                                  km
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Show collection groups - RED */}
                  {collectionGroups.length > 0 && (
                    <div className="cart-item__assigned-groups">
                      <div className="cart-item__groups-label">
                        Collection Groups ({collectionGroups.length}):
                      </div>
                      <div className="cart-item__groups-list">
                        {collectionGroups.map((group) => {
                          const optimizedOrderIds =
                            getOptimizedRouteOrder(group);
                          return (
                            <div
                              key={group.id}
                              className="cart-item__group-tag cart-item__group-tag--collection"
                              style={{
                                color: "#ef4444",
                                borderColor: "#ef4444",
                                backgroundColor: "#ef444420",
                              }}
                            >
                              {group.name} ({optimizedOrderIds.length} orders)
                              {group.deliveryRoute && (
                                <span className="cart-item__group-distance">
                                  •{" "}
                                  {group.deliveryRoute.distanceInKm.toFixed(2)}
                                  km
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Total summary */}
                  <div className="cart-item__summary">
                    <div className="cart-item__summary-items">
                      <div className="cart-item__summary-item">
                        <span className="cart-item__summary-label">
                          Total Groups:
                        </span>
                        <span className="cart-item__summary-value">
                          {assignedGroups.length}
                        </span>
                      </div>
                      <div className="cart-item__summary-item">
                        <span className="cart-item__summary-label">
                          Total Orders:
                        </span>
                        <span className="cart-item__summary-value">
                          {totalDeliveries}
                        </span>
                      </div>
                      <div className="cart-item__summary-item">
                        <span className="cart-item__summary-label">
                          Tentative Groups:
                        </span>
                        <span className="cart-item__summary-value">
                          {tentativeGroups.length}
                        </span>
                      </div>
                      <div className="cart-item__summary-item">
                        <span className="cart-item__summary-label">
                          Collection Groups:
                        </span>
                        <span className="cart-item__summary-value">
                          {collectionGroups.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssignedCartsSection;